'use client';
import type { VisualsProjection } from '@lib/sanity/types/common';
import cn from '@utils/cn';
import { memo } from 'react';
import { SizesProp } from '../../lib/types';
import Embed from './Embed/Embed';
import VisualsImageClient from './Image/VisualsImageClient';
import type { VisualsImageProps } from './Image/renderVisualsImage';
import VideoPlayer from './Video/VideoPlayer';

export { columnsToSizes } from './Image/utils/sizes';

export type VisualsProps = {
  className?: string;
  classNameImage?: string;
  classNameVideo?: string;
  data?: VisualsProjection;
  /** Image loading override; see `VisualsImageProps.loading`. */
  loading?: VisualsImageProps['loading'];
  sizes?: SizesProp;
};

function Visuals({ className, classNameImage, classNameVideo, data, loading, sizes }: VisualsProps) {
  if (!data) {
    return null;
  }

  // Sanity migrations sometimes leave `type` unset; infer it from the available fields.
  const resolvedType =
    data.type ?? (data.embedCode ? 'embedded' : data.icon ? 'icon' : data.default || data.mobile ? 'default' : null);

  if (resolvedType === 'icon') {
    const svg = data.icon?.result;
    if (!svg) {
      return null;
    }

    return <div className={cn('flex', className)} dangerouslySetInnerHTML={{ __html: svg }} />;
  }

  if (resolvedType === 'embedded') {
    if (!data.embedCode) {
      return null;
    }
    return <Embed embedCode={data.embedCode} className={className} />;
  }

  if (resolvedType === 'default') {
    const defaultImage = data.default?.image;
    const mobileImage = data.mobile?.image;
    const defaultVideo = data.default?.video;
    const mobileVideo = data.mobile?.video;

    const hasDefaultImage = Boolean(defaultImage?.image?.asset?.url);
    const hasMobileImage = Boolean(mobileImage?.image?.asset?.url);

    const hasDefaultVideo = Boolean(defaultVideo?.video?.playbackId);
    const hasMobileVideo = Boolean(mobileVideo?.video?.playbackId);

    const hasAnyImage = hasDefaultImage || hasMobileImage;
    const hasAnyVideo = hasDefaultVideo || hasMobileVideo;

    if (hasAnyImage || hasAnyVideo) {
      return (
        <>
          {hasMobileImage ? (
            <VisualsImageClient
              image={mobileImage}
              isEagerLoad={data.isEagerLoad}
              loading={loading}
              sizes={sizes}
              className={cn(className, classNameImage, {
                'md:hidden': hasDefaultVideo || hasDefaultImage,
              })}
            />
          ) : defaultImage?.mobileQuality !== defaultImage?.quality && !hasMobileVideo ? (
            <VisualsImageClient
              image={{ ...defaultImage, quality: defaultImage?.mobileQuality }}
              isEagerLoad={data.isEagerLoad}
              loading={loading}
              sizes={sizes}
              className={cn(className, classNameImage, {
                'md:hidden': hasDefaultVideo || hasDefaultImage,
              })}
            />
          ) : null}

          {hasDefaultImage ? (
            <VisualsImageClient
              image={defaultImage}
              isEagerLoad={data.isEagerLoad}
              loading={loading}
              sizes={sizes}
              className={cn(className, classNameImage, {
                'hidden md:block':
                  hasMobileVideo || hasMobileImage || defaultImage?.mobileQuality !== defaultImage?.quality,
              })}
            />
          ) : null}

          {hasMobileVideo ? (
            <VideoPlayer
              playbackId={mobileVideo?.video?.playbackId}
              filename={mobileVideo?.video?.filename}
              aspect_ratio={mobileVideo?.video?.data?.aspect_ratio}
              thumbTime={mobileVideo?.video?.thumbTime}
              autoPlay={mobileVideo?.autoPlay}
              muted={mobileVideo?.muted}
              loop={mobileVideo?.loop}
              opacity={mobileVideo?.opacity}
              controls={mobileVideo?.controls}
              isEagerLoad={data.isEagerLoad}
              className={cn(className, classNameVideo, {
                'md:hidden': hasDefaultVideo || hasDefaultImage,
              })}
            />
          ) : null}

          {hasDefaultVideo ? (
            <VideoPlayer
              playbackId={defaultVideo?.video?.playbackId}
              filename={defaultVideo?.video?.filename}
              aspect_ratio={defaultVideo?.video?.data?.aspect_ratio}
              thumbTime={defaultVideo?.video?.thumbTime}
              autoPlay={defaultVideo?.autoPlay}
              muted={defaultVideo?.muted}
              loop={defaultVideo?.loop}
              opacity={defaultVideo?.opacity}
              controls={defaultVideo?.controls}
              isEagerLoad={data.isEagerLoad}
              className={cn(className, classNameVideo, {
                'hidden md:block': hasMobileVideo || hasMobileImage,
              })}
            />
          ) : null}
        </>
      );
    }
  }
  return null;
}

export default memo(Visuals);
